/**
 * Every remark ever recorded against a record, on one trail.
 *
 * The ERP keeps its history in a different table per module — review_remark for lost
 * leads, current_remark and target_remark for the cold-calling lists, follow_up for
 * quotations, and a plain notes column for a LinkedIn or telecaller contact. The
 * server reads all of them and folds in the calls logged from this app; this just
 * shows the result, newest first, with nothing hidden behind a button. What the last
 * caller was told is the single most useful thing to have on screen before dialling.
 */
import React, { useState } from 'react';
import {
  FaPhoneAlt, FaRedo, FaCommentDots, FaSnowflake, FaFileAlt, FaUser, FaExclamationTriangle,
} from 'react-icons/fa';
import CallRecording from './CallRecording';

const SOURCE = {
  Call: { icon: FaPhoneAlt, cls: 'accent' },
  'Follow-up': { icon: FaRedo, cls: 'warning' },
  Remark: { icon: FaCommentDots, cls: '' },
  'Cold call': { icon: FaSnowflake, cls: 'accent' },
  Quotation: { icon: FaFileAlt, cls: 'accent' },
  'Contact note': { icon: FaUser, cls: '' },
};

const PREVIEW = 6;

const when = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB");
};

const titleCase = (v) => String(v || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export default function NoteHistory({ notes, loading, error, onRetry, compact }) {
  const [expanded, setExpanded] = useState(false);

  if (loading) return <p className="org-scope-note">Reading the history…</p>;

  if (error) {
    return (
      <div className="org-alert error">
        Could not load the note history.{' '}
        {onRetry && <button type="button" className="scc-linkbtn" onClick={onRetry}>Try again</button>}
      </div>
    );
  }

  const timeline = notes?.timeline || [];

  if (timeline.length === 0) {
    return (
      <p className="org-scope-note">Nothing recorded yet.</p>
    );
  }

  const shown = expanded ? timeline : timeline.slice(0, compact ? 3 : PREVIEW);
  const hidden = timeline.length - shown.length;

  return (
    <div className="scc-notes">
      <div className="scc-notes-counts">
        {notes.counts.total} on record · {notes.counts.erp} from the ERP · {notes.counts.calls} from calls
      </div>

      {shown.map((entry, i) => {
        const meta = SOURCE[entry.source] || SOURCE.Remark;
        const Icon = meta.icon;
        return (
          <div key={`${entry.at || entry.date || i}-${i}`} className={`scc-note${i === 0 ? ' latest' : ''}`}>
            <span className={`scc-note-icon ${meta.cls}`}><Icon size={11} /></span>

            <div className="scc-note-body">
              <div className="scc-note-head">
                <span className="scc-note-source">{entry.source}</span>
                {!!entry.outcome && <span className={`org-pill ${meta.cls}`}>{titleCase(entry.outcome)}</span>}
                {!!entry.status && <span className="org-pill">{titleCase(entry.status)}</span>}
                {entry.pending && <span className="org-pill danger">Not written up</span>}
                {entry.awaitingReview && <span className="org-pill warning">Awaiting review</span>}
                <span className="scc-note-date">{when(entry.date || entry.at)}</span>
              </div>

              <p className="scc-note-text">{entry.remark}</p>
              {!!entry.notes && <p className="scc-note-sub">{entry.notes}</p>}

              <div className="scc-note-foot">
                {!!entry.by && <span>{entry.by}</span>}
                {entry.kind === 'call' && !!entry.durationSeconds && (
                  <span>
                    {Math.floor(entry.durationSeconds / 60)}:
                    {String(entry.durationSeconds % 60).padStart(2, '0')} on the call
                  </span>
                )}
                {/*
                  Played here, never handed to the browser, and in our own transport
                  rather than the one Chrome draws. `preload="none"` so opening a record
                  with twenty calls does not fetch twenty recordings nobody asked to hear.
                */}
                {entry.kind === 'call' && entry.recording?.url && (
                  <span className="scc-note-audio">
                    <CallRecording
                      url={entry.recording.url}
                      name={entry.recording.original_name}
                      durationSeconds={entry.recording.duration_seconds || entry.durationSeconds}
                      compact
                    />
                  </span>
                )}
                {!!entry.nextFollowUpAt && <span>Call again {when(entry.nextFollowUpAt)}</span>}
                {entry.kind === 'call' && entry.syncedToErp === false && (
                  <span className="scc-note-warn"><FaExclamationTriangle size={9} /> not in the ERP</span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {hidden > 0 && (
        <button type="button" className="scc-linkbtn" onClick={() => setExpanded(true)}>
          Show {hidden} older note{hidden === 1 ? '' : 's'}
        </button>
      )}
      {expanded && timeline.length > PREVIEW && (
        <button type="button" className="scc-linkbtn" onClick={() => setExpanded(false)}>
          Collapse
        </button>
      )}

      {!!notes.erpError && (
        <p className="scc-note-warn" style={{ marginTop: 8 }}>Some older remarks could not be read.</p>
      )}
    </div>
  );
}
